import { type Initiative, InitiativeStatus } from '../../api/initiative-client/initiative-client.type';
import type { ProcessAnalysisInitiativeChartData } from '../../api/portfolio-client/portfolio-client.type';
import type { TransformedTarget } from '../../api/targets-client/targets-client.type';
import {
  Measure,
  TimeAllocationType,
  type MeasureMetadata,
  type MeasureValuesOverTime,
  type TimeAllocation,
} from '../../api/work-periods-client/work-periods-client.type';
import {
  type ChartData,
  MetricType,
  MultipleViewMode,
  Tab,
  ViewMode,
} from '../../containers/process-analysis/process-analysis.type';

enum Panel {
  Filters = 'filters',
  Side = 'side',
}

type DataPoint = {
  measure: Measure;
  date: string;
  rawDate: string;
  value: number | null;
  measureData: MeasureValuesOverTime;
  targets: TransformedTarget[];
};

type SelectedTarget = {
  measureId: Measure | string;
  targetId: string;
};

type InitiativeOption = {
  id: string;
  name: string;
};

type EntityFilters = {
  selectedMeasures: Measure[];
  selectedMeasureTrends: Measure[];
  selectedInitiativeStatuses: InitiativeStatus[];
  selectedInitiativeCompletions: InitiativeOption[];
  selectedInitiativeCompletionTrends: InitiativeOption[];
  selectedInitiativeFocus: InitiativeOption[];
  selectedInitiativeFocusTrends: InitiativeOption[];
  selectedCapacityTeams: string[];
  touched?: {
    selectedMeasures?: boolean;
    selectedMeasureTrends?: boolean;
    selectedInitiativeStatuses?: boolean;
    selectedInitiativeCompletions?: boolean;
    selectedInitiativeCompletionTrends?: boolean;
    selectedInitiativeFocus?: boolean;
    selectedInitiativeFocusTrends?: boolean;
    selectedCapacityTeams?: boolean;
  };
};

type FilterEntityKey = `${Tab}:${string}`;

type ComparisonModeFilters = {
  selectedMeasureOverTime: Measure | null;
  selectedMeasureOverTimeTrend: boolean;
  selectedMonthlyAveragesMeasures: Measure[];
};

/**
 * Ids that are still selectable in the currently active scope.
 *
 * A missing key means "not known yet" — the request that resolves that entity
 * list for the active portfolio has not landed, so the matching selection must
 * be left alone instead of being cleared against an empty list.
 */
type SelectionScope = {
  portfolioIds?: Set<string>;
  teamIds?: Set<string>;
  boardIds?: Set<string>;
};

type State = {
  activeTab: Tab;
  viewMode: ViewMode;
  multipleViewMode: MultipleViewMode;
  activePanel: Panel | null;
  startDate: string;
  endDate: string;
  availableMeasures: MeasureMetadata[];
  chartData: ChartData | null;
  initiatives: Initiative[];
  initiativeCompletionData: ProcessAnalysisInitiativeChartData | null;
  initiativeFocusData: ProcessAnalysisInitiativeChartData | null;
  initiativeCompletionMetric: MetricType;
  initiativeFocusMetric: MetricType;
  selectedPortfolios: string[];
  selectedTeams: string[];
  selectedBoards: string[];
  selectedTaskType: string | null;
  // number = a specific estimate; '__none__' = unestimated (points IS NULL);
  // null = no filter ("All story points").
  selectedPoints: number | '__none__' | null;
  selectedAllocationType: TimeAllocationType | null;
  singleModeEntityFilters: Record<FilterEntityKey, EntityFilters>;
  comparisonModeEntityFilters: Record<Tab, ComparisonModeFilters>;
  selectedCapacityTeams: string[];
  selectedDataPoint: DataPoint | null;
  selectedTargets: SelectedTarget[];
  timeAllocations: TimeAllocation[];
  agentVisibleSeries: Record<string, boolean>;
  agentViewMode: string;
  agentChartType: string;
};

export { Panel };
export type {
  ComparisonModeFilters,
  DataPoint,
  EntityFilters,
  FilterEntityKey,
  InitiativeOption,
  SelectedTarget,
  SelectionScope,
  State,
};
